import React, { useLayoutEffect, useRef, FC, useEffect } from 'react'
import ErrorBoundary from '../error/ErrorBoundary'
import './Page.less'
import { isSSR } from 'shared/helpers'
import { Box } from 'front-end/src/components'
import { TOPBAR_HEIGHT } from '../topbar/Topbar'
import { BoxProps } from '@chakra-ui/react'
import { cx } from '@emotion/css'

const pageScrollMemory: Record<string, number> = {}

// React currently throws a warning when using useLayoutEffect on the server.
// To get around it, we can conditionally useEffect on the server (no-op) and
// useLayoutEffect in the browser. We need useLayoutEffect because we want
// `connect` to perform sync updates to a ref to save the latest props after
// a render is actually committed to the DOM.
const useIsomorphicLayoutEffect = isSSR ? useEffect : useLayoutEffect

/**
 * this component remembers it's own scroll position in memory on different locations and sets the position on mount if previously was scrolled to a different position than 0
 */
export const Page: FC<
  BoxProps & {
    className?: string
    withTopbar?: boolean
    memorizeScroll?: boolean
  }
> = ({
  children,
  className,
  withTopbar = false,
  memorizeScroll = false,
  ...rest
}) => {
  const cssClasses = cx('Page', className)
  const divRef = useRef<HTMLDivElement>(null)

  const { pathname } = location
  useIsomorphicLayoutEffect(() => {
    if (memorizeScroll) {
      if (divRef.current && pageScrollMemory[pathname]) {
        divRef.current.scrollTop = pageScrollMemory[pathname]
      }

      return () => {
        if (divRef.current) {
          pageScrollMemory[pathname] = divRef.current.scrollTop
        }
      }
    }
  }, [divRef.current])

  return (
    <Box
      className={cssClasses}
      ref={divRef}
      tabIndex={-1}
      borderTop={withTopbar ? `${TOPBAR_HEIGHT} solid transparent` : undefined}
      {...rest}
    >
      <ErrorBoundary>
        <Box className="Page__inner">{children}</Box>
      </ErrorBoundary>
    </Box>
  )
}

/**
 * makes sure the content doesn't occupy whole width of a screen, but just the invisible "page" in the center os the screen
 */
export function PageContentWrapper({
  children,
  className,
  ...rest
}: BoxProps & {
  children: any
  className?: string
}) {
  return (
    <Box className={cx('Page__content', className)} {...rest}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </Box>
  )
}

export function PagePanel({
  children,
  className
}: {
  children: any
  className?: string
}) {
  return (
    <Box className={cx('Panel', className)}>
      <Box className="Panel__inner">{children}</Box>
    </Box>
  )
}
